import React, { FunctionComponent, useState } from 'react';
import Modal from 'react-modal';
import UpdatePlan from './UpdatePlan';
import { planScopeDisplayConversion, planStateDisplayConversion } from './planApi';
import AuthorizedAction from '../common/AuthorizedAction';
import ColumnCard from '../layout/ColumnCard';
import { modalStyles } from '../layout/Modal';
import { Permission } from '../../auth/getPermissions';
import { FieldOption, formatDateForDisplay } from '../../util/helper-func';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useListData } from '../../list-data/ListDataContext';
import { Plan, PlanState } from '../../api/openapi/rating-config';
import getConfig from '../../config/getConfig';

interface SimplePlanHeaderProps {
  plan: Plan | undefined;
  planId: string | undefined;
}

const PlanHeader: FunctionComponent<SimplePlanHeaderProps> = ({ plan, planId }) => {
  const {
    oci710PlanJurisdictions, // LD client key for oci-710-plan-jurisdictions
    mngr102PoaShareability, // LD client key is mngr-102-poa-shareability
  } = useFlags();
  const { shareablePlans } = getConfig();
  const [editPlanIsOpen, setEditPlanIsOpen] = useState(false);
  const closeEditPlan = () => setEditPlanIsOpen(false);
  const isDeprecated = plan?.planState === PlanState.DEPRECATED;

  const [marketfunction] = useListData(['MARKET_FUNCTION']);
  const jurisdictions = marketfunction?.data[
    'urn:flux:rating-config:market:jurisdictions'
  ] as FieldOption[];

  const resolveJurisdiction = (label: string) => {
    const match = jurisdictions?.find((it) => it.label === label)?.label || '';
    return match ? { label: 'Jurisdiction', value: match } : null;
  };

  const isConnectionSpecificPlan = plan?.planScope === 'CONNECTION_SPECIFIC';
  const jurisdiction = plan?.jurisdiction ? resolveJurisdiction(plan.jurisdiction) : null;

  let content = [
    {
      label: 'Provider',
      value: plan?.providerName || '',
    },
    {
      label: 'Provider reference',
      value: plan?.providerReference || '',
    },
    {
      label: 'Start date',
      value: formatDateForDisplay(plan?.startDate),
    },
    {
      label: 'End date',
      value: formatDateForDisplay(plan?.endDate),
    },
    {
      label: 'Plan scope',
      value: planScopeDisplayConversion(plan?.planScope),
    },
    {
      label: 'Plan status',
      value: planStateDisplayConversion(plan?.planState),
    },
    {
      label: 'Timezone',
      value: plan?.timezone || '',
    },
  ];

  if (shareablePlans && mngr102PoaShareability && isConnectionSpecificPlan) {
    content.push({
      label: 'Shareable',
      value: (plan?.shareable === true ? 'Yes' : 'No') || 'Yes',
    });
  }

  if (oci710PlanJurisdictions && jurisdiction) {
    content = content.slice(0, 2).concat(jurisdiction).concat(content.slice(2));
  }

  return (
    <>
      <ColumnCard
        button={
          <AuthorizedAction
            isDisabled={isDeprecated}
            onClick={() => setEditPlanIsOpen(true)}
            permission={Permission.PLAN_EDIT}
            testId="edit-plan-button"
            title={isDeprecated ? 'Sorry, this plan is deprecated.' : undefined}
          >
            Edit
          </AuthorizedAction>
        }
        content={content}
        id={'plan-header'}
        buttonColumnVisible={false}
      />

      <Modal
        isOpen={editPlanIsOpen}
        onRequestClose={closeEditPlan}
        style={modalStyles}
        shouldCloseOnOverlayClick={false}
      >
        <UpdatePlan close={closeEditPlan} planId={planId} plan={plan} />
      </Modal>
    </>
  );
};

export default PlanHeader;
